import {Button, Chips, DataStatus} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CustomNoticeRelation,
  CustomNoticeRelationSchema,
  RelatedCustomNoticesResourceId,
  useGetRelatedCustomNoticesQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useCanAddCustomNotice} from '../../hooks/useCanAddCustomNotice';
import {getCustomNoticeRelationLabel} from '../../utils/getCustomNoticeRelationLabel';
import {openCreateCustomNoticeDialog} from '../../utils/openCreateCustomNoticeDialog';
import {RelatedCustomNoticeCard} from '../RelatedCustomNoticeCard/RelatedCustomNoticeCard';

const ALL_RELATIONS_CHIP_VALUE = 'all';

interface CustomNoticesTabProps extends TestIdProps {
  resourceId: RelatedCustomNoticesResourceId;
  recordId: string;
}

type SelectedRelation = CustomNoticeRelation | typeof ALL_RELATIONS_CHIP_VALUE;

export function CustomNoticesTab(props: CustomNoticesTabProps) {
  const [selectedRelation, setSelectedRelation] =
    useState<SelectedRelation>(ALL_RELATIONS_CHIP_VALUE);

  const {
    data: notices,
    isLoading,
    isError,
  } = useGetRelatedCustomNoticesQuery({
    resourceId: props.resourceId,
    recordId: props.recordId,
  });

  const {canAddCustomNotice} = useCanAddCustomNotice({
    resourceId: props.resourceId,
    recordId: props.recordId,
  });

  const presentRelations = CustomNoticeRelationSchema.options.filter((relation) =>
    (notices ?? []).some((notice) => notice.relation === relation)
  );

  const activeRelation = presentRelations.includes(selectedRelation)
    ? selectedRelation
    : ALL_RELATIONS_CHIP_VALUE;

  const filteredNotices = (notices ?? []).filter(
    (notice) => activeRelation === ALL_RELATIONS_CHIP_VALUE || notice.relation === activeRelation
  );

  const chipOptions = [
    {value: ALL_RELATIONS_CHIP_VALUE, label: i18n.t('general.labels.all')},
    ...presentRelations.map((relation) => ({
      value: relation,
      label: getCustomNoticeRelationLabel(relation, props.resourceId),
    })),
  ];

  const handleCreate = () =>
    openCreateCustomNoticeDialog({
      resourceId: props.resourceId,
      recordId: props.recordId,
      'data-testid': props['data-testid'],
    });

  const handleRelationChange = (value: string[] | Nullish) => {
    const parsedRelation = CustomNoticeRelationSchema.safeParse(value?.[0]);
    setSelectedRelation(parsedRelation.success ? parsedRelation.data : ALL_RELATIONS_CHIP_VALUE);
  };

  return (
    <VStack spacing={4}>
      <HStack justify="flex-start">
        <Button
          variant="link"
          size="small"
          leftIcon="content/add_circle"
          title={i18n.t('general.customNotices.actions.create')}
          isDisabled={!canAddCustomNotice}
          onClick={handleCreate}
          data-testid={suffixTestId('customNoticesTab-create', props)}
        />
      </HStack>

      <Show when={isNotNilOrEmpty(notices)}>
        <Chips
          size="small"
          options={chipOptions}
          value={[activeRelation]}
          onChange={handleRelationChange}
          data-testid={suffixTestId('customNoticesTab-chips', props)}
        />
      </Show>

      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isNilOrEmpty(notices)}
        emptyMessage={i18n.t('general.customNotices.labels.emptyMessage')}
        minHeight={50}
        data-testid={suffixTestId('customNoticesTab', props)}
      >
        <VStack spacing={3}>
          {filteredNotices.map((notice, index) => (
            <RelatedCustomNoticeCard
              key={notice.id}
              notice={notice}
              hostResourceId={props.resourceId}
              data-testid={suffixTestId(`customNoticesTab-[${index}]`, props)}
            />
          ))}
        </VStack>
      </DataStatus>
    </VStack>
  );
}
